import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';

export interface ActionItem {
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export interface ActionsMenuProps {
  actions: ActionItem[];
  iconSize?: number;
}

export function ActionsMenu({ actions, iconSize = 16 }: ActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 160;
    // Position below the button, aligned to the left edge of the button
    let left = rect.left;
    // If menu would go off the right edge, flip to align right
    if (left + menuWidth > window.innerWidth - 8) {
      left = rect.right - menuWidth;
    }
    // If menu would go off the left edge, clamp
    if (left < 8) left = 8;
    setPosition({ top: rect.bottom + 4, left });
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Filter out disabled actions that shouldn't be shown
  const visibleActions = actions.filter((action) => !action.disabled);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!isOpen) updatePosition();
          setIsOpen(!isOpen);
        }}
        title="פעולות נוספות"
        className="inline-flex min-h-10 min-w-10 flex-shrink-0 items-center justify-center rounded p-1 transition-[background-color,transform] hover:bg-gray-200 active:scale-[0.96]"
      >
        <MoreVertical size={iconSize} className="text-gray-600" />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: position.top, left: position.left }}
            className="z-[9999] min-w-[140px] rounded-lg bg-white py-1 shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.10)]"
          >
            {visibleActions.map((action, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                  setIsOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-right text-sm ${
                  action.variant === 'danger'
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
